/**
 * KeySpawner: manages the periodic spawning of keys in the maze.
 *
 * Keys spawn infrequently on walkable path cells. The spawner respects
 * the global max of keys on the map, forbidden cells (hazards), and
 * minimum distance from the player.
 *
 * Key tier is selected randomly using weighted probabilities.
 * Keys never despawn — they persist until collected.
 */

import KeyItem from '../entities/KeyItem.js';
import { CellType, KeyTier } from '../utils/constants.js';
import {
  TILE_SIZE,
  KEY_SPAWN_INTERVAL,
  MAX_ACTIVE_KEYS,
  MAX_PLAYER_KEYS,
  KEY_NEAR_PLAYER_CHANCE,
  KEY_TIER_WEIGHTS,
  MIN_SPAWN_DISTANCE_FROM_PLAYER,
  SHOP_ITEM_KEY_PENDANT,
  KEY_PENDANT_SPAWN_BONUS,
} from '../config/game-config.js';
import { isItemActive } from './ShopManager.js';

export default class KeySpawner {
  /**
   * @param {Phaser.Scene} scene
   * @param {number[][]} grid - Maze grid (CellType values)
   * @param {Phaser.Physics.Arcade.Group} keyGroup - Physics group for keys
   * @param {{ col: number, row: number }} playerPos - Mutable ref to player position
   * @param {{ col: number, row: number }} exitPos - Exit cell position
   * @param {Set<string>} forbiddenCells - Hazard cells where keys can't spawn
   * @param {import('../entities/Player.js').default} player - Player reference
   * @param {number} playerKeyCount - Current number of keys the player holds
   */
  constructor(scene, grid, keyGroup, playerPos, exitPos, forbiddenCells, player, playerKeyCount) {
    this.scene = scene;
    this.grid = grid;
    this.keyGroup = keyGroup;
    this.playerPos = playerPos;
    this.exitPos = exitPos;
    this.forbiddenCells = forbiddenCells;
    this.player = player;
    this.playerKeyCount = playerKeyCount;

    /** @type {KeyItem[]} Active key items on the map. */
    this.activeItems = [];

    /** @type {Phaser.Time.TimerEvent|null} */
    this._spawnTimer = null;

    /** @type {KeyItem[]|null} Sibling items (food, coins, chests) to avoid overlap. */
    this._siblingFood = null;
    this._siblingCoins = null;
    this._siblingChests = null;
    /** @type {{ item: import('../entities/CompassItem.js').default|null }|null} */
    this._compassRef = null;
  }

  /**
   * Start the key spawn timer.
   */
  start() {
    // Key Pendant: reduce spawn interval by the bonus percentage,
    // effectively increasing spawn frequency by +10%.
    const interval = isItemActive(SHOP_ITEM_KEY_PENDANT)
      ? Math.round(KEY_SPAWN_INTERVAL * (1 - KEY_PENDANT_SPAWN_BONUS))
      : KEY_SPAWN_INTERVAL;

    // Initial spawn after a delay
    this._initialDelay = this.scene.time.delayedCall(3000, () => {
      this._initialDelay = null;
      this._trySpawn();
    });

    this._spawnTimer = this.scene.time.addEvent({
      delay: interval,
      callback: this._trySpawn,
      callbackScope: this,
      loop: true,
    });
  }

  /**
   * Stop the spawner and destroy all active keys.
   */
  stop() {
    if (this._initialDelay) {
      this._initialDelay.destroy();
      this._initialDelay = null;
    }
    if (this._spawnTimer) {
      this._spawnTimer.destroy();
      this._spawnTimer = null;
    }

    for (const item of this.activeItems) {
      if (item && item.active) item.destroy();
    }
    this.activeItems = [];
  }

  /**
   * Called each frame to prune inactive items.
   */
  update() {
    this.activeItems = this.activeItems.filter((item) => item && item.active);
  }

  /**
   * Set sibling item lists for spawn-cell exclusion.
   * @param {any[]} food
   * @param {any[]} coins
   * @param {any[]} chests
   * @param {{ item: import('../entities/CompassItem.js').default|null }|null} [compassRef]
   */
  setSiblings(food, coins, chests, compassRef, totemRef) {
    this._siblingFood = food;
    this._siblingCoins = coins;
    this._siblingChests = chests;
    this._compassRef = compassRef || null;
    this._totemRef = totemRef || null;
  }

  /**
   * Update the player's current key count (to decide whether to spawn).
   * @param {number} count
   */
  setPlayerKeyCount(count) {
    this.playerKeyCount = count;
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /** @private */
  _trySpawn() {
    // Don't spawn if map already has max keys
    if (this.activeItems.length >= MAX_ACTIVE_KEYS) return;

    // Don't spawn if player already has max keys
    if (this.playerKeyCount >= MAX_PLAYER_KEYS) return;

    const cell = this._findSpawnCell();
    if (!cell) return;

    const tier = this._pickTier();
    const x = cell.col * TILE_SIZE + TILE_SIZE / 2;
    const y = cell.row * TILE_SIZE + TILE_SIZE / 2;

    const keyItem = new KeyItem(this.scene, x, y, tier, cell.col, cell.row);
    this.keyGroup.add(keyItem);
    this.activeItems.push(keyItem);
  }

  /**
   * Find a valid spawn cell (walkable, not occupied, not forbidden).
   * @private
   * @returns {{ col: number, row: number }|null}
   */
  _findSpawnCell() {
    const rows = this.grid.length;
    const cols = this.grid[0].length;

    // Build list of walkable path cells
    const pathCells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (this.grid[r][c] === CellType.PATH) {
          pathCells.push({ col: c, row: r });
        }
      }
    }

    // Build occupied set
    const occupied = new Set();
    occupied.add(`${this.playerPos.col},${this.playerPos.row}`);
    occupied.add(`${this.exitPos.col},${this.exitPos.row}`);

    // Player destination (during active tween)
    if (this.player && this.player.getDestination) {
      const dest = this.player.getDestination();
      if (dest) occupied.add(`${dest.col},${dest.row}`);
    }

    // Existing keys
    for (const item of this.activeItems) {
      if (item && item.active) {
        occupied.add(`${item.gridCol},${item.gridRow}`);
      }
    }

    // Sibling items
    const siblingLists = [this._siblingFood, this._siblingCoins, this._siblingChests];
    for (const list of siblingLists) {
      if (list) {
        for (const item of list) {
          if (item && item.active) {
            const ic = item.gridCol != null ? item.gridCol : Math.floor(item.x / TILE_SIZE);
            const ir = item.gridRow != null ? item.gridRow : Math.floor(item.y / TILE_SIZE);
            occupied.add(`${ic},${ir}`);
          }
        }
      }
    }

    // Exclude compass item cell (if spawned and not yet collected)
    if (this._compassRef && this._compassRef.item && this._compassRef.item.active) {
      const ci = this._compassRef.item;
      const cc = ci.gridCol != null ? ci.gridCol : Math.floor(ci.x / TILE_SIZE);
      const cr = ci.gridRow != null ? ci.gridRow : Math.floor(ci.y / TILE_SIZE);
      occupied.add(`${cc},${cr}`);
    }

    // Exclude Threia Totem item cell (if spawned and not yet collected)
    if (this._totemRef && this._totemRef.item && this._totemRef.item.active) {
      const ti = this._totemRef.item;
      const tc = ti.gridCol != null ? ti.gridCol : Math.floor(ti.x / TILE_SIZE);
      const tr = ti.gridRow != null ? ti.gridRow : Math.floor(ti.y / TILE_SIZE);
      occupied.add(`${tc},${tr}`);
    }

    // Filter: not occupied, not forbidden, min distance from player
    const pCol = this.playerPos.col;
    const pRow = this.playerPos.row;

    const candidates = pathCells.filter((cell) => {
      const key = `${cell.col},${cell.row}`;
      if (occupied.has(key)) return false;
      if (this.forbiddenCells.has(key)) return false;
      const dist = Math.abs(cell.col - pCol) + Math.abs(cell.row - pRow);
      return dist >= MIN_SPAWN_DISTANCE_FROM_PLAYER;
    });

    if (candidates.length === 0) return null;

    // Near-player bias (like food/coin spawners)
    if (Math.random() < KEY_NEAR_PLAYER_CHANCE) {
      const near = candidates.filter((c) => {
        const d = Math.abs(c.col - pCol) + Math.abs(c.row - pRow);
        return d >= 3 && d <= 12;
      });
      if (near.length > 0) {
        return near[Math.floor(Math.random() * near.length)];
      }
    }

    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  /**
   * Pick a key tier using weighted probabilities.
   * @private
   * @returns {string}
   */
  _pickTier() {
    const r = Math.random();
    let cumulative = 0;

    cumulative += KEY_TIER_WEIGHTS.metallic;
    if (r < cumulative) return KeyTier.METALLIC;

    cumulative += KEY_TIER_WEIGHTS.copper;
    if (r < cumulative) return KeyTier.COPPER;

    return KeyTier.GOLD;
  }
}
